<!--
  Animated sign-in illustration: an enclosed white printer with side-mounted
  spools and four tall filament loops builds a blue spiral vase while the
  build plate lowers. From the PrintHQ login kit; the canvas drawing code is
  the approved design and is kept as supplied (only types were added). The
  pause/play control follows the app's design system.
-->
<template>
	<div ref="container" class="printhq-printer-scene" :data-theme="resolvedTheme">
		<canvas ref="canvas" role="img" :aria-label="label">
			{{ label }}
		</canvas>
		<div class="scene-overlay"><slot /></div>
		<button
			v-if="showMotionControl && supported"
			type="button"
			class="motion-control"
			:aria-label="isPaused ? 'Play printer animation' : 'Pause printer animation'"
			:disabled="paused"
			@click="toggleMotion"
		>
			<i class="pi" :class="isPaused ? 'pi-play' : 'pi-pause'" aria-hidden="true" />
			<span class="motion-label">{{ isPaused ? 'Play Animation' : 'Pause Animation' }}</span>
		</button>
		<p v-if="!supported" class="canvas-fallback">Printer animation unavailable.</p>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

type Theme = 'auto' | 'light' | 'dark'
type Pt = [number, number]

const props = defineProps({
	theme: { type: String as () => Theme, default: 'auto', validator: (v: string) => ['auto', 'light', 'dark'].includes(v) },
	paused: { type: Boolean, default: false },
	speed: { type: Number, default: 1, validator: (v: number) => Number.isFinite(v) && v > 0 && v <= 3 },
	showMotionControl: { type: Boolean, default: true },
	label: { type: String, default: 'An enclosed white 3D printer builds a blue spiral vase, with side-mounted filament spools and four tall filament loops.' },
})

const container = ref<HTMLDivElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const supported = ref(true)
const systemDark = ref(false)
const reducedMotion = ref(false)
const userPaused = ref<boolean | null>(null)
const resolvedTheme = computed(() => props.theme === 'auto' ? (systemDark.value ? 'dark' : 'light') : props.theme)
const isPaused = computed(() => props.paused || (userPaused.value ?? reducedMotion.value))
let refresh = () => {}
let teardown = () => {}
function toggleMotion() { userPaused.value = !isPaused.value }
function pause() { userPaused.value = true }
function play() { userPaused.value = false }
defineExpose({ pause, play })
watch([resolvedTheme, isPaused, () => props.speed], () => refresh())

onMounted(() => {
	const node = canvas.value
	const context = node?.getContext('2d')
	if (!node || !context) { supported.value = false; return }
	const ctx: CanvasRenderingContext2D = context
	const darkQuery = window.matchMedia('(prefers-color-scheme: dark)')
	const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
	systemDark.value = darkQuery.matches
	reducedMotion.value = motionQuery.matches
	let w = 0, h = 0, dpr = 1, time = 10
	let raf = 0, last: number | null = null, lastPaint = 0, visible = true, disposed = false

	// ── Drawing (approved design; statements as supplied) ──────────────
	function poly(points: Pt[],fill?: string | null,stroke?: string | null,line=1){ctx.beginPath();points.forEach((p,i)=>i?ctx.lineTo(...p):ctx.moveTo(...p));ctx.closePath();if(fill){ctx.fillStyle=fill;ctx.fill()}if(stroke){ctx.strokeStyle=stroke;ctx.lineWidth=line;ctx.stroke()}}
	let scale = 0,ox = 0,oy = 0;
	function p(x: number,y: number,z: number): Pt{return[ox+(x*.95-y*.44)*scale,oy+(x*.08+y*.27)*scale-z*scale]}
	function line(a: Pt,b: Pt,color: string,width=1){ctx.beginPath();ctx.moveTo(...a);ctx.lineTo(...b);ctx.strokeStyle=color;ctx.lineWidth=width*scale;ctx.stroke()}
	function box(x: number,y: number,z: number,a: number,b: number,c: number,colors: string[]){poly([p(x,y,z),p(x+a,y,z),p(x+a,y,z+c),p(x,y,z+c)],colors[0]);poly([p(x+a,y,z),p(x+a,y+b,z),p(x+a,y+b,z+c),p(x+a,y,z+c)],colors[1]);poly([p(x,y+b,z),p(x+a,y+b,z),p(x+a,y+b,z+c),p(x,y+b,z+c)],colors[1]);poly([p(x,y,z+c),p(x+a,y,z+c),p(x+a,y+b,z+c),p(x,y+b,z+c)],colors[2])}
	function ellipse(x: number,y: number,z: number,r: number,fill?: string | null,stroke?: string | null){const points: Pt[]=[];for(let a=0;a<Math.PI*2+.1;a+=.1)points.push(p(x+Math.cos(a)*r,y+Math.sin(a)*r,z));poly(points,fill,stroke)}
	function draw(){
	ctx.setTransform(dpr,0,0,dpr,0,0);ctx.clearRect(0,0,w,h);const isDark=resolvedTheme.value === 'dark';
	const bg=ctx.createLinearGradient(0,0,w,h);bg.addColorStop(0,isDark?'#2e4436':'#eff3e9');bg.addColorStop(1,isDark?'#1d342b':'#dce8dc');ctx.fillStyle=bg;ctx.fillRect(0,0,w,h);
	const glow=ctx.createRadialGradient(w*.7,h*.3,5,w*.7,h*.3,w*.6);glow.addColorStop(0,isDark?'#b9d88c15':'#fffdec90');glow.addColorStop(1,'#fffdec00');ctx.fillStyle=glow;ctx.fillRect(0,0,w,h);
	const mobile=h<500;scale=Math.min(w/590,mobile?.57:.92);ox=w*.48;oy=mobile?h*.63:h*.585;
	ctx.save();ctx.translate(Math.sin(time*.18)*2,Math.cos(time*.17)*1.4);
	// An endless, quiet work surface.
	for(let j=-500;j<600;j+=35){line(p(j,-450,-14),p(j,450,-14),isDark?'#8faa9420':'#70887816',.7);line(p(-500,j,-14),p(500,j,-14),isDark?'#8faa9420':'#70887816',.7)}
	ctx.save();const shadow=p(5,5,-13);ctx.translate(...shadow);ctx.scale(1,.4);const sg=ctx.createRadialGradient(0,0,5,0,0,195*scale);sg.addColorStop(0,'#15392035');sg.addColorStop(1,'#15392000');ctx.fillStyle=sg;ctx.beginPath();ctx.arc(0,0,195*scale,0,Math.PI*2);ctx.fill();ctx.restore();
	const metal=['#282d2e','#151b1c','#484e4f'],pale=['#e9ecea','#c4ccc8','#fafbf8'];
	function sideDisc(x: number,y: number,z: number,r: number,color: string){const pts: Pt[]=[];for(let a=0;a<=Math.PI*2+.05;a+=.065)pts.push(p(x,y+Math.cos(a)*r,z+Math.sin(a)*r));poly(pts,color)}
	function sideRoll(x: number,y: number,z: number,color: string){
		for(let i=0;i<5;i++)sideDisc(x+i,y,z,44,i===0?'#ae9364':'#947c54');
		for(let i=5;i<31;i+=1.1)sideDisc(x+i,y,z,37,color);
		for(let i=7;i<31;i+=2.5){const pts: Pt[]=[];for(let a=0;a<=Math.PI*2+.1;a+=.12)pts.push(p(x+i,y+Math.cos(a)*37,z+Math.sin(a)*37));poly(pts,null,'#ffffff22',.65*scale)}
		for(let i=31;i<=35;i++)sideDisc(x+i,y,z,44,i===35?'#c6b186':'#9c8559');
		sideDisc(x+35.1,y,z,12,'#756343');sideDisc(x+35.2,y,z,7,'#d7dbd4');
		for(let n=0;n<5;n++){const a=n*Math.PI*2/5-time*.16;sideDisc(x+35.3,y+Math.cos(a)*29,z+Math.sin(a)*29,3,'#8f794f');}
	}
	// Far-side spool peeks out from behind the case.
	sideRoll(-152,-17,158,'#d8af64');sideRoll(-152,-17,59,'#d8af64');
	// Deep dark chamber and the white enclosure.
	box(-116,-97,-9,232,194,15,pale);
	box(-116,-97,5,13,194,223,['#dce2de','#1d2526','#454d4d']);
	box(-103,-97,5,219,12,223,metal);
	poly([p(-103,-84,6),p(103,-84,6),p(103,83,6),p(-103,83,6)],'#181e20');
	for(const x of [-83,83]){line(p(x,-76,14),p(x,-76,217),'#647170',4);line(p(x,-75,14),p(x,-75,217),'#bec8c2',1)}
	const cycle=time%26,progress=cycle<22?.23+cycle/22*.77:1;
	const height=115*progress,bed=178-height;
	box(-87,-70,bed-9,174,138,7,['#515b53','#2c352f','#818675']);
	for(let j=-66;j<=66;j+=22){line(p(j,-67,bed-1.8),p(j,65,bed-1.8),'#acb19735',.5);line(p(-84,j,bed-1.8),p(83,j,bed-1.8),'#acb19735',.5)}
	// Build plate descends as the print grows, keeping the nozzle at the gantry.
	ctx.save();ctx.globalAlpha=cycle>24?1-(cycle-24)/2:1;
	function radius(z: number,a: number){return 27+8*Math.sin(z*.047)+3*Math.cos(a*9-z*.052)}
	for(let i=0;i<=Math.floor(height/1.7);i++){const z=i*1.7,pts: Pt[]=[];for(let a=0;a<=Math.PI*2+.06;a+=.065){const r=radius(z,a);pts.push(p(Math.cos(a)*r,Math.sin(a)*r,bed+z))}poly(pts,'hsl(210 66% '+(52+i%2*7)+'%)','#d4ebff38',.4*scale)}
	const rim: Pt[]=[];for(let a=0;a<=Math.PI*2+.06;a+=.065){const r=radius(height,a)-3;rim.push(p(Math.cos(a)*r,Math.sin(a)*r,178))}poly(rim,'#234e80');ctx.restore();
	// Solid right shell, two side-mounted cardboard reels.
	box(103,-97,5,13,194,223,pale);
	for(let j=0;j<8;j++)line(p(116.2,32+j*3.5,35),p(116.2,32+j*3.5,70),'#a5b1ad',1);
	sideRoll(117,-17,158,'#dedfd8');sideRoll(117,-17,59,'#3a89d5');
	// Dark upper rim around the open top.
	box(-116,-97,222,232,16,8,metal);box(-116,-81,222,14,178,8,metal);box(102,-81,222,14,178,8,metal);
	// Four rear tool docks and their tall bowed feed tubes.
	for(let i=0;i<4;i++){
		const x=-76+i*48;
		box(x-15,-81,213,31,32,37,['#353b3c','#252c2d','#525856']);
		box(x-15,-47,216,3,2,28,['#b4a15c','#9d8a40','#e2c765']);
		poly([p(x-9,-47,221),p(x-2,-47,233),p(x+5,-47,221)],'#d1b450');
		line(p(x-5,-46.9,224),p(x-2,-46.9,229),'#383d33',1);
		const start=p(x-5,-59,250),end=p(x+8,-87,244),sway=Math.sin(time*.35+i)*2*scale;
		ctx.beginPath();ctx.moveTo(...start);ctx.bezierCurveTo(start[0]-24*scale+sway,start[1]-177*scale,end[0]+21*scale+sway,end[1]-173*scale,...end);ctx.strokeStyle='#626978';ctx.lineWidth=4*scale;ctx.stroke();ctx.strokeStyle='#c9cad3';ctx.lineWidth=1.5*scale;ctx.stroke();
		box(x-7,-61,247,5,5,7,metal);
	}
	// Moving printhead and exposed motion rails.
	const theta=time*2.8,rr=radius(height,theta),hx=cycle<22?Math.cos(theta)*rr:64,hy=cycle<22?Math.sin(theta)*rr:0;
	for(const y of [hy-17,hy+17]){line(p(-100,y,211),p(100,y,211),'#717979',4);line(p(-100,y,212),p(100,y,212),'#cbd0cb',1)}
	box(hx-16,hy-12,188,32,24,30,pale);
	const fan=p(hx,hy+12.3,203);ctx.beginPath();ctx.ellipse(fan[0],fan[1],6.8*scale,8*scale,-.17,0,Math.PI*2);ctx.fillStyle='#c1a346';ctx.fill();ctx.strokeStyle='#806c2d';ctx.lineWidth=1.4*scale;ctx.stroke();
	poly([p(hx-4,hy-3,188),p(hx+4,hy+3,188),p(hx,hy,179)],'#c6a261');
	// Tinted front window with restrained reflections; print remains visible inside.
	poly([p(-98,97,15),p(98,97,15),p(98,97,190),p(-98,97,190)],'#101c2518','#697371',1.5*scale);
	poly([p(-94,97.2,23),p(-63,97.2,23),p(-24,97.2,185),p(-55,97.2,185)],'#d9edf208');
	line(p(-94,97.5,18),p(94,97.5,18),'#b5beb8',1);
	// White front fascia, narrow jambs, screen and door handle.
	box(-116,83,0,18,15,222,pale);box(98,83,0,18,15,222,pale);
	box(-116,83,190,232,15,32,pale);box(-116,83,0,232,15,13,pale);
	poly([p(25,98.2,196),p(87,98.2,196),p(87,98.2,218),p(25,98.2,218)],'#252e30');
	line(p(33,98.4,207),p(69,98.4,207),'#9cc4ac',1.5);
	line(p(33,98.4,202),p(52,98.4,202),'#58796a',1);
	box(87,99,61,6,5,29,['#515b5c','#303c3d','#75807b']);
	// Top front rim and discreet screw heads.
	box(-116,83,222,232,15,8,metal);
	for(const x of [-109,109])for(const y of [-89,91])ellipse(x,y,230.1,2.7,'#202727','#737d76');
	ctx.restore();
	}

	// ── Lifecycle ───────────────────────────────────────────────────────
	function drawFrame() { if (w > 0 && h > 0) draw() }
	function stop() {
		if (raf) window.cancelAnimationFrame(raf)
		raf = 0
		last = null
		lastPaint = 0
	}
	function shouldAnimate() { return !disposed && visible && !document.hidden && !isPaused.value && w > 0 && h > 0 }
	function tick(now: number) {
		raf = 0
		if (!shouldAnimate()) { last = null; return }
		if (last !== null) time += Math.min((now - last) / 1000, 0.1) * props.speed
		last = now
		if (now - lastPaint >= 1000 / 30) { drawFrame(); lastPaint = now }
		raf = window.requestAnimationFrame(tick)
	}
	refresh = () => {
		if (disposed) return
		stop()
		drawFrame()
		if (shouldAnimate()) raf = window.requestAnimationFrame(tick)
	}
	function resize() {
		w = node!.clientWidth
		h = node!.clientHeight
		dpr = Math.min(window.devicePixelRatio || 1, 2)
		node!.width = Math.round(w * dpr)
		node!.height = Math.round(h * dpr)
		refresh()
	}
	function onDarkChange(event: MediaQueryListEvent) { systemDark.value = event.matches }
	function onMotionChange(event: MediaQueryListEvent) { reducedMotion.value = event.matches; userPaused.value = null }
	darkQuery.addEventListener('change', onDarkChange)
	motionQuery.addEventListener('change', onMotionChange)
	const visibilityHandler = refresh
	document.addEventListener('visibilitychange', visibilityHandler)
	const resizeObserver = new ResizeObserver(resize)
	resizeObserver.observe(node)
	const intersectionObserver = new IntersectionObserver(entries => {
		visible = entries[0]?.isIntersecting ?? true
		refresh()
	})
	intersectionObserver.observe(container.value!)
	window.addEventListener('resize', resize)
	resize()
	teardown = () => {
		disposed = true
		stop()
		resizeObserver.disconnect()
		intersectionObserver.disconnect()
		window.removeEventListener('resize', resize)
		document.removeEventListener('visibilitychange', visibilityHandler)
		darkQuery.removeEventListener('change', onDarkChange)
		motionQuery.removeEventListener('change', onMotionChange)
		refresh = () => {}
	}
})
onBeforeUnmount(() => teardown())
</script>

<style scoped>
.printhq-printer-scene {
	--scene-surface: #eaf0e7;
	position: relative;
	width: 100%;
	overflow: hidden;
	background: var(--scene-surface);
	isolation: isolate;
}
.printhq-printer-scene[data-theme='dark'] {
	--scene-surface: #23382e;
}
canvas {
	display: block;
	width: 100%;
	height: var(--printhq-scene-height, 660px);
}
.scene-overlay {
	position: absolute;
	inset: 0;
	pointer-events: none;
}
/* A small floating control over the scene: elevated at 90% with a blur,
   like the design system's controls over a 3D preview. */
.motion-control {
	position: absolute;
	right: 16px;
	bottom: 16px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	height: 32px;
	padding: 0 10px;
	border: 1px solid var(--border-strong);
	border-radius: 8px;
	color: var(--text-2);
	background: color-mix(in srgb, var(--elevated) 90%, transparent);
	backdrop-filter: blur(4px);
	font: inherit;
	font-size: 12px;
	font-weight: 500;
	transition: color 150ms var(--ease), border-color 150ms var(--ease);
}
.motion-control:hover {
	color: var(--text);
}
.motion-control .pi {
	font-size: 14px;
}
.motion-control:disabled {
	opacity: var(--opacity-disabled);
}
.canvas-fallback {
	position: absolute;
	inset: 45% 20px auto;
	text-align: center;
	color: var(--text-2);
}
@media (pointer: coarse) {
	.motion-control {
		min-width: 44px;
		min-height: 44px;
	}
}
@media (max-width: 720px) {
	canvas {
		height: var(--printhq-scene-mobile-height, 410px);
	}
	.motion-control {
		right: 12px;
		bottom: 12px;
		padding: 0;
		width: 32px;
	}
	.motion-label {
		display: none;
	}
}
</style>
